import { useEffect, useState } from 'react'
import { Column } from 'react-table'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '../../../global/components/table/Table'
import PrimaryButton from '../../../global/components/buttons/primary-button/PrimaryButton'
import { quickReportsWithParams } from '../../../reports/components/api'
import downloadCsvToComputer from '../../../global/utils/downloadCsvToComputer'
import { formatDateYyyyMmDd } from '../../../global/utils/date/date-utils-yyyy-mm-dd'
import BasicFilter from '../../../global/components/table/helpers/filters/BasicFilter'
import CheckboxFilter from '../../../global/components/table/helpers/filters/CheckboxFilter'
import DateFilter from '../../../global/components/table/helpers/filters/DateFilter'
import DropdownFilter from '../../../global/components/table/helpers/filters/DropdownFilter'
import { CasesModal } from '../cases-modal/CasesModal'
import { handleColumnFilterUpdate } from '../../../global/components/table/helpers/multiColumnFilters'
import { ICase } from '../../../../../app/entities/Case'
import fetchDataAndSetTable from '../../../global/components/table/helpers/fetchDataAndSetTable'
import NoFilter from '../../../global/components/table/helpers/filters/NoFilter'
import { IOrder } from '../../../../../app/entities/Order'
import {
  CASE_STATUS,
  INVESTIGATION_STATUS,
} from '../../../global/constants/cases'
import { SSM_PARAMETER_NAMES } from '../../../global/constants/ssm'
import { fetchData } from '../../../global/utils/fetch'
import { getAwsSsmParameter } from '../../api'
import formatCurrency from '../../../global/utils/format-currency-usd'

function formatDate(dateValue: string): string {
  if (!dateValue || dateValue.length === 0) return ''
  const [year, month, day] = dateValue.split('-')
  return `${month}/${day}/${year.slice(-2)}`
}

function CasesTable() {
  const [tableState, setTableState] = useState({
    data: [] as ICase[],
    isLoading: false,
    offSet: 0,
    count: 0,
    pageSize: 100,
  })
  const [refreshKey, setRefreshKey] = useState(0)
  const [columnFilters, setColumnFilters] = useState({})
  const [casesModalIsOpen, setCasesModalIsOpen] = useState(false)
  const [isRoadieChecked, setIsRoadieChecked] = useState(false)
  const [isRiskConnectChecked, setIsRiskConnectChecked] = useState(false)
  const [isTechnicianFaultChecked, setIsTechnicianFaultChecked] =
    useState(false)
  const [caseStatusFilter, setCaseStatusFilter] = useState('')
  const [investigationStatusFilter, setInvestigationStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [riskScoreFilter, setRiskScoreFilter] = useState('')
  const [installDateFilter, setInstallDateFilter] = useState('')
  const [caseTypeItems, setCaseTypeItems] = useState<
    Array<{ key: string; value: string }>
  >([])
  const [caseData, setCaseData] = useState<ICase | null>(null)
  const [orderData, setOrderData] = useState<IOrder | null>(null)

  const initialExportToDate = new Date()
  const initialExportFromDate = new Date()
  initialExportFromDate.setDate(initialExportToDate.getDate() - 7)
  const [exportDates, setExportDates] = useState<{
    fromDate: string
    toDate: string
  }>({
    fromDate: formatDateYyyyMmDd(initialExportFromDate),
    toDate: formatDateYyyyMmDd(initialExportToDate),
  })
  const [isExporting, setIsExporting] = useState(false)

  const caseStatusItems = Object.values(CASE_STATUS).map((status) => ({
    key: status,
    value: status
      .split('_')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' '),
  }))

  const investigationStatusItems = [
    ...Object.entries(INVESTIGATION_STATUS).map(([key, value]) => ({
      key: value,
      value: key
        .split('_')
        .filter(Boolean)
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(' '),
    })),
    { key: 'N/A', value: 'N/A' },
  ]

  function onColumnFilterUpdate(filtersUpdate: object) {
    handleColumnFilterUpdate(filtersUpdate, columnFilters, setColumnFilters)
  }

  const casesEndpoint = `${process.env.REACT_APP_ORIGIN}/cases/all`
  function refetchTable() {
    fetchDataAndSetTable<ICase>({
      endpoint: casesEndpoint,
      queries: [
        { pageSize: tableState.pageSize.toString() },
        { offSet: tableState.offSet.toString() },
        { columnFilters: JSON.stringify(columnFilters) },
      ],
      setTableState,
    })
  }

  useEffect(() => {
    refetchTable()
  }, [columnFilters, tableState.pageSize, tableState.offSet, refreshKey])

  // Fetch case types from SSM
  useEffect(() => {
    async function fetchCaseTypes() {
      try {
        const caseTypesString = await fetchData<string>(
          getAwsSsmParameter(SSM_PARAMETER_NAMES.CASE_TYPES),
        )
        if (caseTypesString) {
          const caseTypesObj = JSON.parse(caseTypesString)
          const items = Object.entries(caseTypesObj).map(([key, value]) => ({
            key,
            value: value as string,
          }))
          setCaseTypeItems(items)
        }
      } catch (error) {
        console.error('Error fetching case types:', error)
      }
    }
    fetchCaseTypes()
  }, [])

  function handleExportDateChange(e: React.ChangeEvent<HTMLInputElement>) {
    setExportDates((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }))
  }

  const isExportRangeInvalid =
    !exportDates.fromDate ||
    !exportDates.toDate ||
    exportDates.fromDate > exportDates.toDate

  async function handleExport() {
    try {
      setIsExporting(true)
      const res = await fetchData<{ csv: string }>(
        quickReportsWithParams('rpt_casesExport', exportDates),
      )
      if (!res.csv) {
        alert('No cases were opened in the selected date range.')
        return
      }
      downloadCsvToComputer(
        `cases-export-${exportDates.fromDate}-to-${exportDates.toDate}.csv`,
        res.csv,
      )
    } catch (error) {
      console.error('Error exporting cases:', error)
      alert('Something went wrong exporting cases. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  function handleClosed() {
    setOrderData(null)
    setCaseData(null)
    setCasesModalIsOpen(false)
  }

  function onRecordClickHandler(row: any) {
    const { original } = row as { original: ICase }
    setCaseData(original)
    setOrderData(original.order ?? null)
    setCasesModalIsOpen(true)
  }

  const columns: Column<ICase>[] = [
    {
      Header: 'Work Order ID',
      accessor: (row) => row.order?.workOrderId,
      Cell: ({ row: { original } }: { row: { original: ICase } }) => {
        const { order } = original
        return <div>{order?.workOrderId || '-'}</div>
      },
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ order: { workOrderId: val } }),
      }),
    },
    {
      Header: 'Install Date',
      accessor: (row) => row.order?.timeSlot?.fromTime,
      Cell: ({ row: { original } }: { row: { original: ICase } }) => {
        const { order } = original
        const fromTime = order?.timeSlot?.fromTime
        if (!fromTime) return <div>-</div>
        const date = new Date(fromTime)
        const formatted = `${date.toLocaleDateString()}`
        return <div>{formatted}</div>
      },
      Filter: DateFilter({
        value: installDateFilter,
        onChange: (val: string) => {
          setInstallDateFilter(val)
          onColumnFilterUpdate({
            order: { timeSlot: { fromDateGenerated: formatDate(val) } },
          })
        },
      }),
    },
    {
      Header: 'Case Status',
      accessor: 'caseStatus',
      Cell: ({ row: { original } }) => {
        const { caseStatus } = original as ICase
        return (
          <div>
            {caseStatus
              ? caseStatus.charAt(0).toUpperCase() +
                caseStatus.slice(1).replace('-', ' ')
              : 'N/A'}
          </div>
        )
      },
      Filter: DropdownFilter({
        value: caseStatusFilter,
        onChange: (val: string) => {
          setCaseStatusFilter(val)
          onColumnFilterUpdate({ caseStatus: val })
        },
        items: caseStatusItems,
      }),
    },
    {
      Header: 'Investigation Status',
      accessor: 'investigationStatus',
      Cell: ({ row: { original } }) => {
        const { investigationStatus } = original as ICase
        return (
          <div>
            {investigationStatus
              ? investigationStatus
                  .split('_')
                  .filter(Boolean)
                  .map(
                    (word) =>
                      word.charAt(0).toUpperCase() +
                      word.slice(1).toLowerCase(),
                  )
                  .join(' ')
              : 'N/A'}
          </div>
        )
      },
      Filter: DropdownFilter({
        value: investigationStatusFilter,
        onChange: (val: string) => {
          setInvestigationStatusFilter(val)
          onColumnFilterUpdate({ investigationStatus: val })
        },
        items: investigationStatusItems,
      }),
    },
    {
      Header: 'Type',
      accessor: 'type',
      Cell: ({ row: { original } }) => {
        const { type } = original as ICase
        const typeItem = caseTypeItems.find((item) => item.key === type)
        return <div>{typeItem?.value || type || '-'}</div>
      },
      Filter: DropdownFilter({
        value: typeFilter,
        onChange: (val: string) => {
          setTypeFilter(val)
          onColumnFilterUpdate({ type: val })
        },
        items: caseTypeItems,
      }),
    },
    {
      Header: 'Roadie',
      accessor: 'isRoadie',
      Cell: ({ row: { original } }) => {
        const { isRoadie } = original as ICase
        return <div>{isRoadie ? 'True' : '-'}</div>
      },
      Filter: CheckboxFilter({
        checked: isRoadieChecked,
        onChange: (checked: boolean) => {
          setIsRoadieChecked(checked)
          onColumnFilterUpdate({ isRoadie: checked ? '1' : '' }) //we do need to have a '' for false otherwise it won't remove the filter
        },
      }),
    },
    {
      Header: 'RiskConnect',
      accessor: 'isRiskConnect',
      Cell: ({ row: { original } }) => {
        const { isRiskConnect, riskConnectCaseNumber } = original as ICase
        return (
          <div>
            {isRiskConnect
              ? riskConnectCaseNumber
                ? riskConnectCaseNumber
                : 'Case Number not Provided'
              : '-'}
          </div>
        )
      },
      Filter: CheckboxFilter({
        checked: isRiskConnectChecked,
        onChange: (checked: boolean) => {
          setIsRiskConnectChecked(checked)
          onColumnFilterUpdate({ isRiskConnect: checked ? '1' : '' })
        },
      }),
    },
    {
      Header: 'Summary',
      accessor: 'summary',
      Cell: ({ row: { original } }: { row: { original: ICase } }) => {
        const { summary } = original
        if (!summary) return <div>-</div>
        const truncated =
          summary.length > 500 ? `${summary.slice(0, 500)}...` : summary
        return <div>{truncated}</div>
      },
      Filter: BasicFilter({
        onEnter: (val: string) => onColumnFilterUpdate({ summary: val }),
      }),
    },
    {
      Header: 'Technician Name',
      accessor: (row) => row.technician?.name,
      Cell: ({ row: { original } }: { row: { original: ICase } }) => {
        const { technician } = original
        return <div>{technician?.name || '-'}</div>
      },
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ technician: { name: val } }),
      }),
    },
    {
      Header: 'Tech Fault',
      accessor: 'isTechnicianFault',
      Cell: ({ row: { original } }) => {
        const { isTechnicianFault } = original as ICase
        return <div>{isTechnicianFault ? 'True' : '-'}</div>
      },
      Filter: CheckboxFilter({
        checked: isTechnicianFaultChecked,
        onChange: (checked: boolean) => {
          setIsTechnicianFaultChecked(checked)
          onColumnFilterUpdate({ isTechnicianFault: checked ? '1' : '' })
        },
      }),
    },
    {
      Header: 'Customer Name',
      accessor: (row) =>
        row.order?.customer
          ? `${row.order.customer.firstName || ''} ${
              row.order.customer.lastName || ''
            }`.trim()
          : '',
      Cell: ({ row: { original } }: { row: { original: ICase } }) => {
        const { order } = original
        const customer = order?.customer
        if (!customer) return <div>-</div>
        const fullName = `${customer.firstName || ''} ${
          customer.lastName || ''
        }`.trim()
        return <div>{fullName || '-'}</div>
      },
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ order: { customer: { firstName: val } } }),
      }),
    },
    {
      Header: 'Total Cost',
      accessor: 'damageCost',
      Cell: ({ row: { original } }) => {
        const { damageCost, riskConnectCost } = original as ICase
        const totalCost = (damageCost || 0) + (riskConnectCost || 0)
        return (
          <>
            {totalCost === 0 ? (
              <div>{'-'}</div>
            ) : (
              <div>{formatCurrency(totalCost)}</div>
            )}
          </>
        )
      },
      Filter: NoFilter(),
    },
    {
      Header: 'Risk Score',
      accessor: 'riskScore',
      Cell: ({ row: { original } }) => {
        const { riskScore } = original as ICase
        return <div>{riskScore ?? '-'}</div>
      },
      Filter: DropdownFilter({
        value: riskScoreFilter,
        onChange: (val: string) => {
          setRiskScoreFilter(val)
          onColumnFilterUpdate({ riskScore: val })
        },
        items: [
          { key: '1', value: '1' },
          { key: '2', value: '2' },
          { key: '3', value: '3' },
          { key: '4', value: '4' },
          { key: '5', value: '5' },
        ],
      }),
    },
  ]

  return (
    <div style={{ width: '100%' }}>
      <CasesModal
        isOpen={casesModalIsOpen}
        caseData={caseData}
        handleClose={handleClosed}
        orderData={orderData!}
        refetch={() => setRefreshKey(refreshKey + 1)}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '8px',
        }}
        title='Exports cases opened within the date range, not cases with an install in the date range.'
      >
        <TextField
          label='From'
          name='fromDate'
          type='date'
          value={exportDates.fromDate}
          onChange={handleExportDateChange}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label='To'
          name='toDate'
          type='date'
          value={exportDates.toDate}
          onChange={handleExportDateChange}
          InputLabelProps={{ shrink: true }}
        />
        <PrimaryButton
          buttonName='Export'
          onClick={handleExport}
          disabled={isExporting || isExportRangeInvalid}
        />
        {isExporting && <CircularProgress size={24} />}
      </div>
      <Table
        columns={columns}
        data={tableState.data}
        isLoading={tableState.isLoading}
        onRowClick={onRecordClickHandler}
        isPaginated
        pagination={{ setTableState, tableState }}
        isTableTopperPresent={false}
      />
    </div>
  )
}

export default CasesTable
